import React from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Save, Search, Zap } from 'lucide-react';
import { PLACEHOLDERS } from './helpers';

/**
 * Advanced endpoint / payload editor. Hidden by default for native platforms.
 */
export function PlatformAdvancedForm({
    platform,
    form,
    onUpdate,
    onSave,
    onDiscover,
    onOpenCurl,
    discovering,
}) {
    const key = platform.platform;
    const f = form || {};

    return (
        <Card className="p-4 border-dashed">
            <div className="flex items-center justify-between mb-3">
                <div>
                    <div className="font-display font-semibold text-sm">Advanced \u2014 endpoint configuration</div>
                    <div className="text-[11px] text-muted-foreground">
                        {platform.is_native
                            ? `Not needed for ${platform.name} \u2014 leave blank to use the native bridge.`
                            : `${platform.name} is not natively supported yet. Configure the endpoint manually or use the importers.`}
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenCurl(key)}
                        className="bg-emerald-50 border-emerald-200 hover:bg-emerald-100 text-emerald-900"
                        data-testid={`admin-cookie-curlimport-${key}-button`}
                    >
                        <Zap className="h-3.5 w-3.5 mr-1" /> Paste cURL
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onDiscover(key)}
                        disabled={discovering}
                        data-testid={`admin-cookie-discover-${key}-button`}
                    >
                        <Search className="h-3.5 w-3.5 mr-1" /> {discovering ? 'Scanning\u2026' : 'Discover'}
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => onSave(key)}
                        className="bg-[#0E2A47] hover:bg-[#0B223A]"
                        data-testid={`admin-cookie-save-${key}-button`}
                    >
                        <Save className="h-3.5 w-3.5 mr-1" /> Save
                    </Button>
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="space-y-1">
                    <Label className="text-xs">Mode</Label>
                    <Select value={f.mode || 'api'} onValueChange={(v) => onUpdate(key, 'mode', v)}>
                        <SelectTrigger className="w-full text-xs" data-testid={`admin-cookie-mode-${key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="api">JSON API</SelectItem>
                            <SelectItem value="form">HTML form</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1">
                    <Label className="text-xs">HTTP method</Label>
                    <Select value={f.method || 'POST'} onValueChange={(v) => onUpdate(key, 'method', v)}>
                        <SelectTrigger className="w-full text-xs" data-testid={`admin-cookie-method-${key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {['POST', 'GET', 'PUT', 'PATCH'].map((m) => (
                                <SelectItem key={m} value={m}>{m}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1 lg:col-span-2">
                    <Label className="text-xs">
                        Recharge endpoint URL <span className="text-muted-foreground">(supports {PLACEHOLDERS.join(', ')})</span>
                    </Label>
                    <Input
                        className="font-mono text-xs"
                        value={f.endpoint_template || ''}
                        onChange={(e) => onUpdate(key, 'endpoint_template', e.target.value)}
                        placeholder="https://api.example.com/recharge"
                        data-testid={`admin-cookie-endpoint-${key}`}
                    />
                </div>
                <div className="space-y-1 lg:col-span-2">
                    <Label className="text-xs">Discovery URL (for the Discover button)</Label>
                    <Input
                        className="font-mono text-xs"
                        value={f.discovery_url || ''}
                        onChange={(e) => onUpdate(key, 'discovery_url', e.target.value)}
                        data-testid={`admin-cookie-discovery-url-${key}`}
                    />
                </div>
                <div className="space-y-1 lg:col-span-2">
                    <Label className="text-xs">Payload template (JSON)</Label>
                    <Textarea
                        rows={5}
                        className="font-mono text-[11px]"
                        value={f.payload_template || ''}
                        onChange={(e) => onUpdate(key, 'payload_template', e.target.value)}
                        data-testid={`admin-cookie-payload-${key}`}
                    />
                </div>
                <div className="space-y-1 lg:col-span-2">
                    <Label className="text-xs">Extra headers (JSON)</Label>
                    <Textarea
                        rows={3}
                        className="font-mono text-[11px]"
                        value={f.headers_json || '{}'}
                        onChange={(e) => onUpdate(key, 'headers_json', e.target.value)}
                        data-testid={`admin-cookie-headers-${key}`}
                    />
                </div>
            </div>
        </Card>
    );
}

export default PlatformAdvancedForm;
